export class ObjectUtils {
	/**
	 * Safely traverse through an object with a dot notated path string
	 * @param path
	 * @param obj
	 */
	public static resolveDotNotationPath(path, obj) {
		return path?.split('.').reduce((prev, curr) => (prev ? prev[curr] : undefined), obj || self);
	}

	public static resolvePath(path: string, obj: any): any {
		if (!path) return undefined;

		// 1) Convert bracket notation of the form:
		//      ["some property"]  =>  .some property
		//      ['some property']  =>  .some property
		//    This ensures that afterwards we only deal with dot-notation
		const normalizedPath = path
		  // bracket with single quotes -> .group1
		  .replace(/\['([^']*)'\]/g, '.$1')
		  // bracket with double quotes -> .group1
		  .replace(/\["([^"]*)"\]/g, '.$1');

		// 2) Split on '.' to get the property segments
		//    and filter out any accidental empty strings
		//    (in case the path starts or ends with '.')
		const segments = normalizedPath
		  .split('.')
		  .filter(Boolean);

		// 3) Traverse the object using the segments
		return segments.reduce((prev, curr) => {
		  if (prev === undefined || prev === null) return undefined;
		  return prev[curr];
		}, obj);
	}

	public static createFromDotNotationPath(path) {
		return path.split('.').reduceRight((prev, curr) => ({ [curr]: prev }), {});
	}

	public static unfreeze(obj: any) {
		return structuredClone(obj);
	}

	public static getPropertyByName(input: Object | any[], keyName: string) {
		for (const [k, v] of Object.entries(input)) {
			if (k === keyName) {
				return v;
			}
			if (Array.isArray(v)) {
				const result = this.getPropertyByName(v, keyName);
				if (result !== -1) {
					return result;
				}
			}
			if (typeof v === 'object') {
				const result = this.getPropertyByName(v, keyName);
				if (result !== -1) {
					return result;
				}
			}
		}
		return -1;
	}

	public static cleanObject(obj, includeEmptyStrings = false) {
		// If the input is not an object, return it as it is
		if (typeof obj !== 'object' || obj === null) {
			return obj;
		}

		// Iterate over each property of the object
		Object.keys(obj).forEach(key => {
			// Recursively clean the nested object
			obj[key] = this.cleanObject(obj[key]);

			// If the cleaned value is an object and is either empty or has only undefined values
			if (typeof obj[key] === 'object' && obj[key] !== null) {
				// Check if the object is empty or all properties are undefined
				const isEmpty = Object.keys(obj[key]).length === 0;
				// const allUndefined = Object.values(obj[key]).every(value => value === undefined);
				const allUndefinedOrEmptyString = Object.values(obj[key]).every(
					value => value === undefined || (value === '' && !includeEmptyStrings)
				);

				// If the object is empty or all properties are undefined, delete the property
				if (isEmpty || allUndefinedOrEmptyString) {
					delete obj[key];
				}
			} else if(obj[key] == null) { // if the property is not an object but it's null|undefined, it should be removed as well
				delete obj[key];
			}
		});

		return obj;
	}

	public static getLocale(value: string, localeData: any): string {
		let locale = window.navigator.language;
		if (localStorage.getItem('localeCode')) {
			locale = localStorage.getItem('localeCode');
		}

		// See if the localeData has a value for the current locale.
		if (localeData && localeData[locale]) {
			return localeData[locale] as string; // Force to string, because we should have passed the correct type.
		} else {
			return value;
		}
	}

	/**
	 * Compare two objects by their properties, excluding specified properties.
	 * If `exactMatch` is true, it checks if both objects have the same keys and values.
	 * If `exactMatch` is false, it checks if all properties in `obj1`
	 * match those in `obj2`, ignoring any additional properties in `obj2`.
	 * @param obj1
	 * @param obj2
	 * @param excludedProperty List of properties in dot notation to exclude from comparison (e.g., ['property1', 'nested.property2'])
	 * @param exactMatch
	 */
	public static compareObjectsByProperties(obj1: Record<string, any>, obj2: Record<string, any>, excludedProperty: string[] = [], exactMatch = false) {
		const currentLevelExclusions = excludedProperty.filter(prop => !prop.includes('.'));
		const filterKeys = (obj: Record<string, any>) => Object.keys(obj).filter(key => !currentLevelExclusions.includes(key));
		const keys1 = excludedProperty.length ? filterKeys(obj1) : Object.keys(obj1);

		if (!exactMatch) {
			return keys1.every(key => obj1[key] === obj2[key]);
		}

		const keys2 = excludedProperty.length ? filterKeys(obj2) : Object.keys(obj2);


		return keys1.length === keys2.length && keys1.every(key => {
			const value1 = obj1[key];
			const value2 = obj2[key];

			// Check if both are empty arrays
			if (Array.isArray(value1) && Array.isArray(value2) && value1.length === 0 && value2.length === 0) {
				return true;
			}

			// Check for nested objects
			if (value1 && typeof value1 === 'object' && value2 && typeof value2 === 'object') {
				const objectExcludeProperties = excludedProperty.filter(prop => prop.startsWith(key + '.')).map(prop => prop.slice(key.length + 1));
				return ObjectUtils.compareObjectsByProperties(value1, value2, objectExcludeProperties, exactMatch);
			}

			// Default comparison
			return value1 === value2;
		});
	}

	/**
	 * Remove specified properties from an object by setting them to `null` or `undefined`.
	 * @param obj The object to remove properties from.
	 * @param keysToRemove List of keys to remove from the object.
	 * @param resetTo If provided, sets the removed properties to this value (null or undefined). Defaults to undefined.
	 */
	public static removePropertiesFromObject<T>(obj: T, keysToRemove: (keyof T)[], resetTo?: null | undefined): any {
		// create a reset object with keys to exclude set to undefined
		const resetObj = keysToRemove.reduce((acc, key) => {
			acc[key] = resetTo;
			return acc;
		}, {} as Partial<T>);

		return {
			...obj,
			...resetObj
		}
	}
}
